import type { ReactNode } from 'react';

/**
 * A small Markdown renderer.
 *
 * It builds React elements rather than an HTML string, so there is no
 * `dangerouslySetInnerHTML` and nothing to sanitise. That matters more than it looks:
 * project notes are shared (§5.4), so the author of a description is not always the
 * person reading it, and an HTML-string renderer would put another member's markup into
 * your page.
 *
 * Supported: headings, bold, italic, inline code, links, bullet and numbered lists,
 * task checkboxes, fenced code, block quotes, horizontal rules, and tables. Not
 * supported: images, footnotes, nested lists, inline HTML. The gap is deliberate — this
 * exists so a packing list and a table of train times render, not to be a Markdown
 * implementation.
 */

export type Block =
  | { kind: 'heading'; level: number; text: string }
  | { kind: 'paragraph'; text: string }
  | { kind: 'code'; language: string; lines: string[] }
  | { kind: 'quote'; lines: string[] }
  | { kind: 'rule' }
  | { kind: 'list'; ordered: boolean; items: { text: string; checked: boolean | null }[] }
  | { kind: 'table'; header: string[]; rows: string[][] };

const TABLE_ROW = /^\s*\|(.+)\|\s*$/;
/** `| --- | :--: |` — the line that makes the row above a header. */
const TABLE_DIVIDER = /^\s*\|?[\s:|-]+\|[\s:|-]*$/;

const splitRow = (line: string): string[] =>
  line
    .replace(/^\s*\|/, '')
    .replace(/\|\s*$/, '')
    .split('|')
    .map((cell) => cell.trim());

/**
 * Group lines into blocks. Deliberately line-based: no nesting to get wrong.
 *
 * Exported because this is where the decisions live — is that a table or a paragraph
 * beginning with a pipe — and it is pure data, so it can be tested without a renderer.
 */
export function parseBlocks(source: string): Block[] {
  const lines = source.replace(/\r\n/g, '\n').split('\n');
  const blocks: Block[] = [];
  let paragraph: string[] = [];

  const flushParagraph = () => {
    if (paragraph.length > 0) {
      blocks.push({ kind: 'paragraph', text: paragraph.join(' ') });
      paragraph = [];
    }
  };

  for (let i = 0; i < lines.length; i += 1) {
    const line = lines[i]!;

    if (line.trim() === '') {
      flushParagraph();
      continue;
    }

    const fence = /^```(\w*)\s*$/.exec(line);
    if (fence) {
      flushParagraph();
      const body: string[] = [];
      i += 1;
      while (i < lines.length && !/^```\s*$/.test(lines[i]!)) {
        body.push(lines[i]!);
        i += 1;
      }
      blocks.push({ kind: 'code', language: fence[1] ?? '', lines: body });
      continue;
    }

    // A table is a row followed by a divider; anything else beginning with | is a
    // paragraph that happens to start with a pipe.
    if (TABLE_ROW.test(line) && i + 1 < lines.length && TABLE_DIVIDER.test(lines[i + 1]!)) {
      flushParagraph();
      const header = splitRow(line);
      const rows: string[][] = [];
      i += 2;
      while (i < lines.length && TABLE_ROW.test(lines[i]!)) {
        rows.push(splitRow(lines[i]!));
        i += 1;
      }
      i -= 1;
      blocks.push({ kind: 'table', header, rows });
      continue;
    }

    const heading = /^(#{1,6})\s+(.*)$/.exec(line);
    if (heading) {
      flushParagraph();
      blocks.push({ kind: 'heading', level: heading[1]!.length, text: heading[2]! });
      continue;
    }

    if (/^\s*(---+|\*\*\*+)\s*$/.test(line)) {
      flushParagraph();
      blocks.push({ kind: 'rule' });
      continue;
    }

    if (/^\s*>\s?/.test(line)) {
      flushParagraph();
      const body: string[] = [];
      while (i < lines.length && /^\s*>\s?/.test(lines[i]!)) {
        body.push(lines[i]!.replace(/^\s*>\s?/, ''));
        i += 1;
      }
      i -= 1;
      blocks.push({ kind: 'quote', lines: body });
      continue;
    }

    const bullet = /^\s*[-*+]\s+(.*)$/.exec(line);
    const numbered = /^\s*\d+[.)]\s+(.*)$/.exec(line);
    if (bullet || numbered) {
      flushParagraph();
      const ordered = numbered !== null;
      const items: { text: string; checked: boolean | null }[] = [];

      while (i < lines.length) {
        const match = ordered
          ? /^\s*\d+[.)]\s+(.*)$/.exec(lines[i]!)
          : /^\s*[-*+]\s+(.*)$/.exec(lines[i]!);
        if (!match) break;

        const box = /^\[([ xX])\]\s+(.*)$/.exec(match[1]!);
        items.push(
          box
            ? { text: box[2]!, checked: box[1]!.toLowerCase() === 'x' }
            : { text: match[1]!, checked: null },
        );
        i += 1;
      }
      i -= 1;
      blocks.push({ kind: 'list', ordered, items });
      continue;
    }

    paragraph.push(line.trim());
  }

  flushParagraph();
  return blocks;
}

/**
 * Inline formatting.
 *
 * Code spans are taken first and never looked inside, so `**not bold**` written inside
 * backticks stays literal — which is the whole reason someone reaches for backticks.
 */
function renderInline(text: string, keyPrefix: string): ReactNode[] {
  const out: ReactNode[] = [];
  // Only `match[0]` (the whole token) is read below, and `|` already spans from one
  // alternative to the next, so no alternative needs a group of its own around it.
  const pattern = /`[^`]+`|\[[^\]]+\]\([^)\s]+\)|\*\*[^*]+\*\*|__[^_]+__|\*[^*]+\*|_[^_]+_/g;

  let last = 0;
  let match: RegExpExecArray | null;
  let n = 0;

  while ((match = pattern.exec(text)) !== null) {
    if (match.index > last) out.push(text.slice(last, match.index));
    const token = match[0];
    const key = `${keyPrefix}-i${n++}`;

    if (token.startsWith('`')) {
      out.push(<code key={key}>{token.slice(1, -1)}</code>);
    } else if (token.startsWith('[')) {
      const link = /^\[([^\]]+)\]\(([^)\s]+)\)$/.exec(token)!;
      const href = link[2]!;
      // Only http(s), mailto, and a path on this site. A `javascript:` href in a shared note
      // would be a scripted link dressed up as text.
      //
      // The path branch refuses a second `/` or a `\` after the first: `//other.example` and
      // `/\other.example` both start with a slash, and a browser resolves both to *another
      // host*, so they rendered as a link that looked like it stayed here and did not. Refused
      // rather than drawn as external, because nobody writing a note means a protocol-relative
      // link, and `https://` says the same thing plainly. A CSP cannot cover this: it does not
      // govern where a navigation goes.
      const safe = /^(https?:|mailto:|\/(?![/\\]))/i.test(href);
      out.push(
        safe ? (
          <a key={key} href={href} target="_blank" rel="noopener noreferrer">
            {link[1]}
          </a>
        ) : (
          <span key={key}>{link[1]}</span>
        ),
      );
    } else if (token.startsWith('**') || token.startsWith('__')) {
      out.push(<strong key={key}>{token.slice(2, -2)}</strong>);
    } else {
      out.push(<em key={key}>{token.slice(1, -1)}</em>);
    }
    last = match.index + token.length;
  }

  if (last < text.length) out.push(text.slice(last));
  return out;
}

export function renderMarkdown(source: string): ReactNode[] {
  return parseBlocks(source).map((block, index) => {
    const key = `b${index}`;
    switch (block.kind) {
      case 'heading': {
        const Tag = `h${Math.min(block.level + 2, 6)}` as 'h3';
        return <Tag key={key}>{renderInline(block.text, key)}</Tag>;
      }
      case 'paragraph':
        return <p key={key}>{renderInline(block.text, key)}</p>;
      case 'code':
        return (
          <pre key={key}>
            <code>{block.lines.join('\n')}</code>
          </pre>
        );
      case 'quote':
        return <blockquote key={key}>{renderInline(block.lines.join(' '), key)}</blockquote>;
      case 'rule':
        return <hr key={key} />;
      case 'list': {
        const items = block.items.map((item, n) => (
          <li key={`${key}-${n}`} className={item.checked === null ? undefined : 'md-task'}>
            {item.checked !== null && (
              <input type="checkbox" checked={item.checked} readOnly tabIndex={-1} />
            )}
            {renderInline(item.text, `${key}-${n}`)}
          </li>
        ));
        return block.ordered ? <ol key={key}>{items}</ol> : <ul key={key}>{items}</ul>;
      }
      case 'table':
        return (
          <div key={key} className="md-table-wrap">
            <table>
              <thead>
                <tr>
                  {block.header.map((cell, n) => (
                    <th key={`${key}-h${n}`}>{renderInline(cell, `${key}-h${n}`)}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {block.rows.map((row, r) => (
                  <tr key={`${key}-r${r}`}>
                    {block.header.map((_, c) => (
                      <td key={`${key}-r${r}c${c}`}>
                        {renderInline(row[c] ?? '', `${key}-r${r}c${c}`)}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        );
    }
  });
}
